import clamp from 'lodash/clamp';
import round from 'lodash/round';

import { HexColor, RGBColor } from '~src/model/color';

export const valueToHex = (c: number) => {
  const hex = c.toString(16);
  return hex.length === 1 ? `0${hex}` : hex;
};

export const rgbToHex = ({ r = 0, g = 0, b = 0 }: RGBColor) => `#${valueToHex(r)}${valueToHex(g)}${valueToHex(b)}`;

const toUnqualifiedHex = (h: string): HexColor => {
  if (h[0] === '#') {
    return toUnqualifiedHex(h.slice(1));
  }
  return h.length === 6 ? h : h.length === 3 ? `${h[0]}${h[0]}${h[1]}${h[1]}${h[2]}${h[2]}` : '';
};

export const hexToRGB = (h: HexColor, alpha?: number): RGBColor => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(toUnqualifiedHex(h));
  return {
    r: result ? parseInt(result[1], 16) : 0,
    g: result ? parseInt(result[2], 16) : 0,
    b: result ? parseInt(result[3], 16) : 0,
    ...(alpha ? { a: alpha } : {}),
  };
};

export const applyAlpha = (h: HexColor, alpha: number) => `${h}${valueToHex(Math.round(alpha * 255))}`;

export const anyColorToRGBA = (color?: HexColor | RGBColor, alpha: number = 0) => {
  if (!color) {
    return { r: 255, g: 255, b: 255, a: 1 };
  }

  const { r = 0, g = 0, b = 0, a } = typeof color === 'string' ? hexToRGB(color, alpha) : color;

  return { r, g, b, ...(a && { a }) };
};

export const calculateHoverColor = (color?: HexColor) => {
  const { r, g, b, a } = anyColorToRGBA(color);
  const newValues = {
    r: clampRGBValue(r * 0.8),
    g: clampRGBValue(g * 0.8),
    b: clampRGBValue(b * 0.8),
    ...(a && { a }),
  };

  return rgbToHex(newValues);
};

export const calculateDisabledColor = (color: HexColor) => applyAlpha(color, 0.4);

export const findExistingIDsAndColors = (existingColors: Record<number, string>) =>
  Object.entries(existingColors).reduce(
    (acc, [key, value]) => ({ ...acc, ids: acc.ids.concat(Number(key)), colors: acc.colors.concat(value) }),
    { ids: [], colors: [] } as { ids: number[]; colors: string[] }
  );

export const clampRGBValue = (value: number) => round(clamp(value, 0, 255));
